import type { Dictionary } from "./types";

export const en = {
  common: {
    signIn: "Sign in",
    signOut: "Sign out",
    settings: "Settings",
    help: "Help",
    add: "Add",
    account: "Account",
    cancel: "Cancel",
    apply: "Apply",
    dismiss: "Dismiss",
    retry: "Retry",
    save: "Save",
    loading: "Loading",
    openWorkspace: "Open workspace",
    language: "Language",
    signInWith: (provider) => `Sign in with ${provider}`
  },
  dataTable: {
    rows: (count) => `${count} ${count === 1 ? "row" : "rows"}`,
    columns: "Columns"
  },
  entities: {
    person: "People",
    organisation: "Companies",
    event: "Events",
    opportunity: "Opportunities",
    note: "Notes",
    personSingular: "Contact",
    organisationSingular: "Company",
    eventSingular: "Event",
    opportunitySingular: "Opportunity",
    noteSingular: "Note",
    nodeFallback: "Node"
  },
  nav: {
    overview: "Overview",
    graph: "Graph",
    people: "People",
    companies: "Companies",
    events: "Events",
    schedule: "Schedule",
    signals: "Commercial",
    current: "Current",
    privateAgent: "Agent",
    workspace: "Workspace",
    connectors: "Connectors"
  },
  login: {
    tagline: "Every conversation, kept as a living relationship graph.",
    headingIn: "Welcome back",
    headingOut: "Sign in to Salesko",
    subIn: "Your session is ready.",
    subOut: "Use your Salesko account to open your workspace.",
    subOutUnavailable: "Salesko account sign-in is not configured yet.",
    continueWith: (provider) => `Continue with ${provider}`,
    openWithout: "Continue without signing in",
    notConfigured: "Sign-in provider is not configured.",
    fineLead: "By continuing, you agree to our ",
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    and: " and ",
    period: "."
  },
  workspace: {
    contactsIntelligenceWorkspaceAriaLabel: "Salesko contacts intelligence workspace",
    headerAriaLabel: "Workspace header",
    searchPlaceholder: "Search people, companies, events...",
    searchAriaLabel: "Search graph",
    searchResultsLabel: "Graph search results",
    tenantControlsAriaLabel: "Tenant controls",
    opportunitySignalsAriaLabel: "Opportunity signals",
    canonicalRegistryAriaLabel: "Canonical entity registry",
    selectedEntityAriaLabel: "Selected entity",
    detailPanelToggleAriaLabel: "Toggle detail panel",
    deleteConversationConfirm: "Delete this conversation? This can't be undone.",
    currentCount: (count) => `${count} current`,
    savedCount: (count) => `${count} saved`,
    searchingSaved: "Searching saved",
    noCurrentMatch: "No current-frame node matches.",
    savedFrames: "Saved frames",
    canonicalMatches: "Canonical matches",
    unavailable: "Unavailable",
    searchingSavedFrames: "Searching saved frames...",
    noSavedMatch: "No saved-frame node matches.",
    viewsLabel: "Views",
    accountMenuLabel: "Account menu",
    noProviderConfigured: "No sign-in provider configured",
    quotaUnlimited: "Unlimited",
    quotaUnlimitedTooltip: "Unlimited Agent messages on your plan",
    quotaRemaining: (remaining, limit) =>
      `${remaining} of ${limit} daily Agent messages left · resets 00:00 UTC`
  },
  accountSurface: {
    ariaLabel: "Account",
    syncCopy: "Sync frames, signals, and tenant access.",
    tenantLabel: (tenantId) => `tenant ${tenantId}`,
    noProviderConfigured: "No sign-in provider is configured.",
    sessionGrantReady: "Session grant ready"
  },
  authProviders: {
    signInOptions: "Sign in options"
  },
  workspaceError: {
    eyebrow: "Workspace error",
    title: "Salesko graph workspace failed to render.",
    fallback: "The workspace hit an unexpected rendering error.",
    retry: "Retry workspace"
  },
  graphStage: {
    graphFieldAriaLabel: "Graph field",
    relationshipGraphLabel: "Relationship graph",
    viewModeLabel: "Graph view mode",
    show2dTitle: "Show an Obsidian-style 2D force graph",
    timelineEnabledTitle: "Lay events along time",
    timelineDisabledTitle: "Need at least 2 events to build a timeline",
    createShareAriaLabel: "Create read-only share link",
    sharedReadOnlyTitle: "Shared view is read-only",
    copyShareTitle: "Copy read-only share link",
    openSettingsAriaLabel: "Open workspace settings",
    openSettingsTitle: "Open settings",
    openGuideAriaLabel: "Open workspace guide",
    openGuideTitle: "Open workspace guide",
    toggleFullscreenAriaLabel: "Toggle graph fullscreen",
    toggleFullscreenTitle: "Toggle fullscreen",
    loadingGraphField: "Loading graph field...",
    agentFrameWaiting: "Agent frame waiting.",
    graphNodesAriaLabel: "Graph nodes",
    nodeEdgeCount: (nodeCount, edgeCount) => `${nodeCount} nodes, ${edgeCount} edges.`,
    projectedGraphCount: (visibleNodeCount, sourceNodeCount, visibleEdgeCount, sourceEdgeCount) =>
      `Showing ${visibleNodeCount} of ${sourceNodeCount} nodes and ${visibleEdgeCount} of ${sourceEdgeCount} relationships.`,
    layoutDegraded: "Showing the immediate graph layout because background refinement is unavailable.",
    selectNode: (label) => `Select ${label}`,
    selectRelationship: (label) => `Select relationship ${label}`,
    flatGraphAriaLabel: "2D force graph",
    timelineGraphAriaLabel: "Timeline relationship graph",
    timelineLabel: "Timeline",
    timelineMomentsAriaLabel: "Timeline moments",
    previousTimelineMoment: "Previous event",
    nextTimelineMoment: "Next event",
    timelinePosition: (position, total) => `Event ${position} of ${total}`,
    dateUnknown: "Date unknown",
    noLinkedEntities: "No linked entities",
    linkedEntityCount: (count) => `${count} linked ${count === 1 ? "entity" : "entities"}`,
    timelineNeedsTwoEvents: "Timeline needs at least two events.",
    addDatedInteraction: "Add dated interaction",
    setOpeningMomentTitle: "Save this event as the frame's opening moment",
    setOpeningMoment: "Set opening moment",
    graphControlsAriaLabel: "Graph view controls",
    recenter: "Recenter",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    navigatorAriaLabel: "Graph navigator. Drag to rotate the graph.",
    warningsCount: (count) => ` ${count} warnings.`
  },
  frameIo: {
    dismissStatus: "Dismiss frame import/export/share status",
    exporting: (format) => `Exporting ${format} artifact.`,
    importing: (fileName, format) => `Importing ${fileName} as a new ${format} frame.`,
    importingSimple: (fileName) => `Importing ${fileName}.`,
    creatingShareLink: "Creating read-only share link.",
    creatingShareLinkShort: "Creating share link.",
    downloaded: (format) => `Downloaded ${format} artifact`,
    downloadedFile: (filename) => `Downloaded ${filename}.`,
    copiedShareLink: "Copied read-only share link",
    shareLinkReadyNoCopy: "Read-only share link ready; browser copy unavailable",
    shareLinkReady: "Share link ready.",
    shareExpires: (date) => `expires ${date}.`,
    importedSummary: (fileName, nodeCount, edgeCount) =>
      `Imported ${fileName}: ${nodeCount} nodes, ${edgeCount} edges.`,
    importedOpening: (fileName) => `Imported ${fileName}. Opening frame.`,
    fileFallback: "file",
    obsidianFilesLabel: (count) => `${count} Obsidian files`,
    errors: {
      exportFailed: "Frame export failed.",
      shareLinkFailed: "Share link could not be created.",
      importFailed: "Frame import failed.",
      exportNeedsCommittedFrame: "Generate and commit a graph frame before exporting.",
      shareNeedsCommittedFrame: "Generate and commit a graph frame before sharing.",
      jsonImportFailed: "JSON import failed.",
      obsidianImportFailed: "Obsidian import failed."
    },
    fileErrors: {
      jsonTooLarge: "JSON graph import is limited to 5 MB.",
      jsonInvalid: (fileName) => `${fileName} is not valid JSON.`,
      jsonInvalidShape: "JSON import must be a Salesko graph export or relationship graph file.",
      obsidianEmptySelection: "Select at least one Obsidian Markdown file or export artifact.",
      obsidianJsonTooLarge: "Obsidian JSON artifact import is limited to 5 MB.",
      obsidianJsonInvalidShape: "Obsidian JSON import must be a Salesko Obsidian graph export.",
      obsidianMarkdownTooLarge: (fileName) => `${fileName} exceeds the 100 KB Obsidian file limit.`
    }
  },
  changeSet: {
    dismissStatus: "Dismiss change-set status",
    importingFile: (fileName) => `Importing ${fileName} into a pending change set.`,
    creatingFor: (sourceLabel) => `Creating pending change set for ${sourceLabel}.`,
    pendingSummary: (changeSetId, sourceLabel, commandCount, nodeCount, edgeCount) =>
      nodeCount === undefined || edgeCount === undefined
        ? `Pending change set ${changeSetId} for ${sourceLabel}: ${commandCount} commands.`
        : `Pending change set ${changeSetId} for ${sourceLabel}: ${commandCount} commands, ${nodeCount} nodes, ${edgeCount} edges.`,
    applying: (changeSetId, sourceLabel) => `Applying change set ${changeSetId} for ${sourceLabel}.`,
    reverting: (changeSetId, sourceLabel) => `Reverting change set ${changeSetId} for ${sourceLabel}.`,
    applied: (changeSetId, projectionStatus) => `Applied change set ${changeSetId}; projection ${projectionStatus}.`,
    reverted: (changeSetId, projectionStatus) => `Reverted change set ${changeSetId}; projection ${projectionStatus}.`,
    apply: "Apply",
    revert: "Revert",
    pendingPanel: (changeSetId, sourceLabel) => `Pending change set ${changeSetId} from ${sourceLabel}.`,
    updatingSource: (action, sourceLabel) => `${action} ${sourceLabel}.`,
    sourceUpdated: (sourceLabel) => `${sourceLabel} updated. Opening frame.`,
    errors: {
      historyFailed: "Change-set history failed.",
      auditLogFailed: "Change-set audit log failed.",
      auditExportFailed: "Audit export failed.",
      csvImportFailed: "CSV import failed.",
      createFailed: "Create change set failed.",
      applyFailed: "Apply change set failed.",
      revertFailed: "Revert change set failed.",
      csvNeedsCommittedFrame: "Generate and commit a graph frame before importing CSV.",
      createNeedsCommittedFrame: "Generate and commit a graph frame before creating graph changes.",
      csvTooLarge: "CSV import is limited to 100 KB in this API slice.",
      csvEmpty: "CSV file is empty."
    },
    confirm: {
      csvImport: (fileName) => `Import ${fileName} as a pending change set for the current graph frame?`,
      create: (sourceLabel) => `Create a pending change set for ${sourceLabel}?`,
      apply: (summary) => `Apply ${summary} to this graph frame?`,
      applyImportSummary: (commandCount, sourceLabel) => `${commandCount} commands from ${sourceLabel}`,
      applyChangeSetSummary: (changeSetId) => `change set ${changeSetId}`,
      revert: (changeSetId, sourceLabel) => `Revert change set ${changeSetId} for ${sourceLabel}?`
    },
    sources: {
      nodeEdit: (label) => `node edit: ${label}`,
      relationship: (sourceLabel, targetLabel) => `relationship: ${sourceLabel} -> ${targetLabel}`,
      relationshipEdit: (label) => `relationship edit: ${label}`,
      relationshipDelete: (label) => `relationship delete: ${label}`,
      relationshipTouchpoint: (label) => `relationship touchpoint: ${label}`,
      timelineReplay: (label) => `timeline replay: ${label}`,
      leadStageMoveIn: (label) => `move to current: ${label}`,
      leadStageMoveOut: (label) => `remove from current: ${label}`,
      targetFallback: "target",
      currentMoment: "current moment"
    }
  },
  leftRail: {
    entitiesLabel: "Entities",
    toggleEntity: (label) => `Toggle ${label}`,
    relationshipStrengthLabel: "Frame relationship strength",
    strengthWeak: "Weak",
    strengthMedium: "Medium",
    strengthStrong: "Strong",
    recentFrames: "Recent frames",
    loadingFrames: "Loading committed frames…",
    frameHistoryUnavailable: "Frame history is unavailable right now.",
    noFramesYet: "Committed frames will appear here.",
    frameNodeCount: (count) => `${count} nodes`,
    frameLoadError: (frameId) => `Could not load frame ${frameId}.`,
    recentConversations: "Recent conversations",
    newConversationAriaLabel: "Start a new conversation",
    newConversationTitle: "New conversation",
    loadingConversations: "Loading conversations…",
    conversationHistoryUnavailable: "Conversation history is unavailable right now.",
    noConversationsYet: "Conversations will appear here.",
    threadMessageCount: (count) => `${count} messages`,
    deleteConversationTitle: "Delete conversation",
    deleteConversationAriaLabel: (title) => `Delete conversation ${title}`,
    conversationLoadError: (threadId) => `Could not load conversation ${threadId}.`
  },
  onboarding: {
    eyebrowGuide: "Workspace guide",
    heading: "Start from a graph frame",
    graphSummary: (nodeCount, edgeCount) => `${nodeCount} nodes · ${edgeCount} relationships in view`,
    firstGraphSectionLabel: "First graph workflow",
    eyebrowFirstGraph: "First graph",
    captureContextTitle: "Capture context",
    captureContextBody: "Paste meeting notes, call notes, or deal context into the agent composer.",
    reviewGraphTitle: "Review the graph",
    reviewGraphBody: "Review candidates, edit nodes or relationships, then apply pending changes.",
    keepFrameTitle: "Keep the frame",
    keepFrameBody: "Signed-in workspaces can restore committed frames from Recent frames.",
    openAgent: "Open agent",
    startInGraph: "Start in graph",
    workspaceStatusLabel: "Workspace status",
    signedInStatus: "Signed in",
    signInToPersistStatus: "Sign in to persist frames",
    frameHistoryActiveStatus: "Frame history active",
    noCommittedFrameStatus: "No committed frame yet",
    readOnlySharedViewStatus: "Read-only shared view",
    workspaceEditableStatus: "Workspace is editable",
    signInOptionsLabel: "Workspace sign in options"
  },
  agentChat: {
    graphChat: "Graph Chat",
    openAgentInputAriaLabel: "Open agent input",
    agentInputAriaLabel: "Agent input",
    headline: "Turn context into a graph",
    subtitle: "Free · instant · uses the context you provide",
    signedInTitle: "Signed in",
    signInOptionsAriaLabel: "Sign in options",
    collapseAgentInputAriaLabel: "Collapse agent input",
    collapseTitle: "Collapse",
    agentConversationAriaLabel: "Agent conversation",
    exampleNoteAriaLabel: "Example note",
    exampleOnly: "Example only",
    useExample: "Use this example",
    seeExample: "See an example",
    messageLabel: "Message",
    messagePlaceholder: "Paste notes or ask Graph Chat to update the graph, or type @ to mention entities...",
    mentionListAriaLabel: "Mention graph entities",
    mentionWithText: (text) => `Mention "${text}"`,
    mentionCurrentEntity: "Mention current graph entity",
    noMentionMatches: "No current-frame entity matches.",
    composerHint: "People, events, companies, opportunities",
    generating: "Generating...",
    send: "Send",
    structuringGraphFrame: "Structuring graph frame",
    requestFailed: "Request failed",
    needsReview: "Needs review",
    executed: "Executed",
    nodesLabel: "Nodes",
    edgesLabel: "Edges",
    peopleLabel: "People",
    eventsLabel: "Events",
    sourceModel: "Model",
    sourceRules: "Rules",
    sourceAgent: "Agent",
    genericAgentName: "Salesko Agent",
    genericAgentSubtitle: "Runs on Salesko",
    handoff: (agentName) => `Hand off to ${agentName}`,
    streamStructuring: "Using Graph Chat to structure your relationship context.",
    streamAuthRequired: "Graph Chat needs model extraction before this note can be saved.",
    streamRebuilding: "Reading that context and rebuilding the contacts graph.",
    streamSeekerStarting: "Reviewing your graph read-only, getting ready to talk it through.",
    previewStreamFailed: "Agent preview stream failed.",
    noteIntakeExtractionFailure: "Agent LLM extraction did not produce a graph frame; note was not persisted.",
    noteIntakeExtractionFailureReason: (reasons) => `Reason: ${reasons}`,
    noteIntakeCapturing: "Capturing note through the intake pipeline.",
    modeToggleAriaLabel: "Conversation mode",
    modeWriterLabel: "Writer",
    modeSeekerLabel: "Seeker",
    modeWriterHint: "Writer: this conversation updates your graph.",
    modeSeekerHint: "Seeker: read-only discussion, no graph changes.",
    seekerMessagePlaceholder: "Ask Seeker to discuss or analyze your relationship graph — read-only, nothing is changed..."
  },
  detail: {
    emptyTitle: "No entity selected",
    selfMarkedTitle: "This node is marked as you — click to clear",
    selfMarkTitle: "Mark this node as you (the ego-center of your graph)",
    selfYou: "You",
    selfSet: "Set as you",
    leadStageInTitle: "Mark this node as a Current lead",
    leadStageOutTitle: "This node is in Current — click to remove",
    leadStageInLabel: "Move to Current",
    leadStageOutLabel: "In Current",
    dismissCalendarEvent: "Dismiss event",
    dismissCalendarEventPending: "Dismissing…",
    dismissCalendarEventDone: "Event dismissed from graph.",
    dismissCalendarEventError: "Could not dismiss this calendar event. Please try again.",
    about: "About",
    followUp: "Follow-up",
    contact: "Contact",
    relationshipContext: "Relationship context",
    personalDetails: "Personal details",
    relationshipStageField: "Relationship stage",
    relationshipSourceField: "Relationship source",
    contactActionsAriaLabel: "Contact actions",
    emailAction: "Email",
    callAction: "Call",
    staleByDays: (days) => `${days} days overdue`,
    noAboutYet: "No summary yet.",
    source: "Source",
    editNodeTitle: "Edit selected node",
    commitBeforeEditNodesTitle: "Commit a frame before editing nodes",
    editNodeAriaLabel: "Edit selected node",
    labelField: "Label",
    summaryField: "Summary",
    evidenceField: "Evidence",
    roleField: "Role",
    companyField: "Company",
    stageField: "Stage",
    sourceField: "Source",
    timeTextField: "Time text",
    genderField: "Gender",
    birthdayField: "Birthday",
    interestsField: "Interests",
    emailField: "Email",
    phoneField: "Phone",
    locationField: "Location",
    industryField: "Industry",
    sizeField: "Company size",
    revenueField: "Annual revenue",
    websiteField: "Website",
    fieldOptionUnset: "Not set",
    genderOptions: {
      male: "Male",
      female: "Female",
      other: "Other",
      undisclosed: "Prefer not to say"
    },
    industryOptions: {
      technology: "Technology",
      finance: "Finance",
      healthcare: "Healthcare",
      manufacturing: "Manufacturing",
      retail: "Retail",
      education: "Education",
      media: "Media",
      realEstate: "Real estate",
      energy: "Energy",
      consulting: "Consulting",
      government: "Government",
      other: "Other"
    },
    saveAsChangeSet: "Save as change set",
    canonicalIdentityAriaLabel: "Canonical identity",
    identity: "Identity",
    editIdentityTitle: "Edit canonical identity",
    commitBeforeEditIdentityTitle: "Commit a frame before editing identity",
    editIdentityAriaLabel: "Edit canonical identity",
    identityActionRunning: (action, sourceLabel) => `${action} ${sourceLabel}`,
    identityActionSaved: (action) => `${action} saved`,
    splitLabel: "Split label",
    splitSelected: "Split selected",
    rekeyLabel: "Re-key label",
    rekeyIdentity: "Re-key identity",
    mergeTarget: "Merge target",
    noSameTypeTarget: "No same-type target",
    signalsPrefix: (signals) => `Signals: ${signals}`,
    mergeIdentity: "Merge identity",
    reviewCandidateAriaLabel: "Review candidate",
    review: "Review",
    reviewCount: (count) => (count === 1 ? "1 candidate needs review" : `${count} candidates need review`),
    extractionConfidence: "Extraction confidence",
    reviewReasonAgentFlagged: "Flagged by the agent",
    reviewReasonLowConfidence: (percent) => `${percent}% confidence`,
    accept: "Accept",
    discard: "Discard",
    linkedEntities: "Linked entities",
    viewAllLinkedTitle: (label) => `View all ${label} in the canonical registry`,
    viewAllLinkedAriaLabel: (count, label) => `View all ${count} ${label} in the canonical registry`,
    viewAllLinked: (count) => `View all ${count}`,
    createRelationshipTitle: "Create relationship",
    commitBeforeCreateRelationshipTitle: "Commit a frame with at least two nodes first",
    createRelationshipAriaLabel: "Create relationship",
    targetField: "Target",
    typeField: "Type",
    relationshipTypeRelatedTo: "Related to",
    relationshipTypeMetAt: "Met at",
    relationshipTypeWorksAt: "Works at",
    relationshipTypeAttended: "Attended",
    relationshipTypeIntroducedBy: "Introduced by",
    relationshipTypeOpportunityAt: "Opportunity at",
    relationshipTypeMentioned: "Mentioned",
    strengthField: (percent) => `Strength ${percent}%`,
    saveRelationship: "Save relationship",
    editingRelationship: (endpoint, edgeId) => `Editing ${endpoint} · ${edgeId}`,
    saveRelationshipChanges: "Save relationship changes",
    existingRelationshipsAriaLabel: "Existing relationships",
    editRelationshipAriaLabel: (label) => `Edit relationship ${label}`,
    deleteRelationshipAriaLabel: (label) => `Delete relationship ${label}`,
    editRelationshipTitle: "Edit relationship",
    commitBeforeEditRelationshipsTitle: "Commit a frame before editing relationships",
    deleteRelationshipTitle: "Delete relationship",
    commitBeforeDeleteRelationshipsTitle: "Commit a frame before deleting relationships",
    linkedGroupsAriaLabel: "Linked entity groups",
    noLinkedYet: (label) => `No linked ${label} yet.`,
    frameCommit: "Frame commit",
    sharedStatus: "shared",
    previewStatus: "preview",
    idleStatus: "idle",
    readOnlyGraphFrame: "read-only graph frame",
    defaultGraphFrame: "default graph frame",
    waitingForGraphFrame: "waiting for graph frame",
    advanced: "Advanced",
    relationshipDirectionOut: "from this entity",
    relationshipDirectionIn: "to this entity",
    identityActionMerge: "Merge",
    identityActionRekey: "Re-key",
    identityActionSplit: "Split"
  },
  relationshipIntel: {
    ariaLabel: "Relationship intelligence",
    title: "Relationship intelligence",
    signInCommittedFrame: "Open a committed frame while signed in to load relationship intelligence.",
    addAnotherEntity: "Add another entity to inspect introduction paths.",
    targetLabel: "Intro path target",
    pathsTo: (label) => `Paths to ${label}`,
    introductionPaths: "Introduction paths",
    loadingPaths: "Loading introduction paths",
    pathsUnavailable: "Relationship paths are unavailable right now.",
    noPath: "No introduction path found for this pair.",
    commonNeighbor: "common neighbor",
    direct: "direct",
    seenInFrames: (count) => `seen in ${count} frames`,
    staleReminders: "Stale relationship reminders",
    loadingStale: "Loading stale relationships",
    remindersUnavailable: "Relationship reminders are unavailable right now.",
    noStaleForEntity: (count) => `No stale relationships for this entity; ${count} elsewhere in this tenant.`,
    noStaleOlderThan: (days) => `No stale relationships older than ${days} days.`,
    lastSeenStaleBy: (lastSeen, days) => `last seen ${lastSeen} · stale by ${days} days`,
    recordTouchpointTitle: "Record a touchpoint as a pending change set",
    openLastSeenFrameTitle: "Open the frame where this relationship was last seen",
    logTouchpoint: "Log touchpoint",
    openInFrame: "Open in frame"
  },
  changeSetHistory: {
    ariaLabel: "Change set history",
    title: "Change sets",
    refreshAriaLabel: "Refresh change set history",
    refreshTitle: "Refresh change set history",
    noCommit: "Generate and commit a frame to see mutation history.",
    loading: "Loading change-set history",
    empty: "No change sets recorded for this frame.",
    downloaded: (filename) => `Downloaded ${filename}`,
    auditLog: "Audit log",
    auditLogFor: (changeSetId) => `Audit log for ${changeSetId}`,
    loadingAuditLog: "Loading audit log",
    emptyAuditLog: "No audit log events for this change set.",
    graphMutation: "Graph mutation",
    commandCount: (count) => `${count} commands`,
    exportAuditAriaLabel: (changeSetId) => `Export audit artifact for ${changeSetId}`,
    inspectAuditAriaLabel: (changeSetId) => `Inspect audit log for ${changeSetId}`,
    exportAuditTitle: "Export audit JSON",
    auditLogTitle: "Audit log",
    columnChangeSet: "Change set",
    columnStatus: "Status",
    columnCommands: "Commands",
    columnUpdated: "Updated",
    columnActions: "Actions",
    columnEvent: "Event",
    columnCreated: "Created",
    columnDetails: "Details",
    auditCreated: "created",
    auditApplied: "applied",
    auditReverted: "reverted",
    privateAgentSource: "Private Agent"
  },
  signals: {
    title: "Opportunity inbox",
    eyebrow: "Across saved graph history",
    inboxSummary: (pending, saved) => `Showing ${pending} pending · ${saved} saved`,
    unavailable: "Opportunity inbox unavailable",
    viewModeAriaLabel: "Opportunity inbox view mode",
    cardViewTitle: "Card view",
    tableViewTitle: "Table view",
    cards: "Cards",
    table: "Table",
    signInToLoad: "Sign in to load your private opportunity inbox.",
    loading: "Loading opportunity inbox...",
    empty: "No pending or saved opportunities.",
    actionsFor: (title) => `Actions for ${title}`,
    openSourceFor: (title) => `Open source frame for ${title}`,
    openSourceFrame: "Open source frame",
    updated: "Updated",
    evidence: "Evidence",
    source: "Source",
    confidence: (score) => `Confidence ${score}%`,
    whySurfaced: "Why this surfaced",
    followUpRequested: (date) => `Unscheduled follow-up added ${date}`,
    details: "Details",
    reasonUnavailable: "No reason was recorded for this opportunity.",
    evidenceAvailable: "Available",
    evidenceUnavailable: "Unavailable",
    evidenceSpanCount: (count) => `${count} evidence span${count === 1 ? "" : "s"}`,
    relatedEntities: "Related entities",
    relatedFrame: "Related frame",
    sourceAvailable: "Source frame available",
    sourceUnavailable: "Unavailable",
    suggestedNextAction: "Suggested next action",
    status: {
      pending: "Pending",
      saved: "Saved",
      dismissed: "Dismissed",
      archived: "Archived"
    },
    actions: {
      dismiss: "Dismiss",
      save: "Save opportunity",
      saved: "Saved",
      followUp: "Add unscheduled follow-up",
      followUpAdded: "Follow-up added"
    },
    columns: {
      actions: "Actions",
      evidence: "Evidence",
      score: "Confidence",
      source: "Source",
      status: "Status",
      title: "Title",
      updatedAt: "Updated"
    }
  },
  activityFeed: {
    surfaceAriaLabel: "Commercial activity and opportunities",
    closeSurface: "Close commercial panel",
    tabsAriaLabel: "Commercial surfaces",
    activityTab: "Activity Feed",
    inboxTab: "Opportunity Inbox",
    title: "Activity Feed",
    eyebrow: "Recorded facts and changes",
    pageSummary: (count, hasNext) => hasNext ? `Showing ${count} recent entries` : `${count} entries`,
    unavailable: "Activity Feed unavailable",
    loading: "Loading Activity Feed…",
    empty: "No recorded activity yet.",
    recordedAt: "Recorded",
    occurredAt: "Occurred",
    timeUnavailable: "Not recorded",
    backfilled: "Historical",
    backfilledLabel: "Backfilled",
    backfilledValue: (backfilled) => backfilled ? "Yes" : "No",
    correction: "Correction",
    supersedes: "Replaces an earlier recorded entry",
    evidence: "Evidence",
    evidenceStates: {
      available: "Available",
      sourceUnavailable: "Source unavailable",
      redacted: "Redacted"
    },
    entryTypes: {
      eventOccurred: "Event occurred",
      opportunitySurfaced: "Opportunity surfaced",
      transactionRecorded: "Transaction recorded",
      transactionStatusChanged: "Transaction status changed",
      transactionCorrected: "Transaction corrected"
    },
    subjectTypes: {
      event: "Event",
      opportunity: "Opportunity",
      transaction: "Transaction"
    },
    inspectSource: (index) => `Inspect source ${index}`,
    inspectSourceFor: (title, index) => `Inspect source ${index} for ${title}`,
    inspectTransaction: "View transaction",
    inspectTransactionFor: (title) => `View transaction for ${title}`,
    openTransactionGraph: "Open in graph",
    openTransactionGraphFor: (title) => `Open in graph: ${title}`,
    loadOlder: "Load older",
    loadingOlder: "Loading older entries…",
    sourceDetailTitle: "Exact source",
    transactionDetailTitle: "Current transaction",
    transactionGraphTitle: "Transaction graph",
    transactionGraphReadOnly: "Current fact and its evidence-backed connections · read-only",
    closeSourceDetail: "Close source detail",
    closeTransactionDetail: "Close transaction detail",
    closeTransactionGraph: "Close transaction graph",
    loadingSource: "Loading exact source…",
    loadingTransaction: "Loading current transaction…",
    loadingTransactionGraph: "Loading transaction graph…",
    transactionGraphUnavailable: "The transaction graph is unavailable.",
    selectTransactionGraphNode: "Select a node to inspect its evidence.",
    exactQuote: "Exact quote",
    evidenceSpan: "Evidence span",
    sourceFrame: "Source frame",
    sourceNode: "Source node",
    openSourceFrame: "Open source frame",
    kind: "Kind",
    externalId: "External ID",
    status: "Status",
    amount: "Amount",
    parties: "Parties",
    provenance: "Provenance",
    acceptedAt: "Accepted",
    evidenceText: "Evidence",
    authority: "Authority",
    sourceFact: "Source fact",
    sourceFrames: "Source frames",
    evidenceSpans: "Evidence spans",
    sourceReferences: "Source references",
    transactionKinds: {
      contract: "Contract",
      order: "Order",
      invoice: "Invoice",
      payment: "Payment"
    },
    transactionStatuses: {
      committed: "Committed",
      settled: "Settled",
      cancelled: "Cancelled"
    }
  },
  settings: {
    navigationAriaLabel: "Settings navigation",
    sectionsAriaLabel: "Settings sections",
    title: "Settings",
    workspaceSettings: "Workspace settings",
    description: "Manage your account, agent defaults, and data — all in one place.",
    signOut: "Sign out",
    billingSuccess: "Plan activated. Deep Research is now available; your Agent credits were added.",
    billingCancel: "Checkout canceled. Your plan hasn't changed.",
    sidebar: {
      account: "Account",
      accountDescription: "Name your workspace",
      billing: "Billing",
      billingDescription: "Messaging addon plan",
      credits: "Credits",
      creditsDescription: "Agent credit balance",
      computers: "Computers",
      computersDescription: "Connect the computer your agent runs on",
      privateAgent: "Private Agent",
      privateAgentDescription: "Name it, bind it, choose its runtime",
      agent: "Agent",
      agentDescription: "Language, region, channels",
      data: "Data",
      dataDescription: "Export your workspace data",
      advanced: "Advanced",
      advancedDescription: "File import and frame artifacts"
    }
  },
  billing: {
    ariaLabel: "Billing",
    title: "Messaging channels",
    signIn: "Sign in to manage your messaging addon.",
    active: "Messaging addon active",
    pendingCancel: "cancels at period end",
    renews: (date) => `renews ${date}`,
    upsell: "Telegram, WeChat, and Line delivery are included with any paid plan.",
    viewPlans: "View plans"
  },
  credits: {
    ariaLabel: "Agent credits",
    title: "Agent credits",
    signIn: "Sign in to view your Agent credits.",
    upgrade: "Upgrade to a paid plan to unlock Deep Research.",
    byok: "Deep Research runs on your own computer with your provider sign-in or key; Salesko credits are not used.",
    available: "credits available",
    empty: "No credit activity yet.",
    loadError: "Could not load your credit balance. Try again shortly."
  },
  computers: {
    ariaLabel: "Computers",
    title: "Computers",
    description: "Your Private Agent runs on a computer you connect here. Salesko coordinates the work; your computer does it.",
    signIn: "Sign in to connect a computer.",
    connect: "Connect a computer",
    stepInstallTitle: "1 · Install and pair",
    stepInstallBody: "Create a one-time code, then paste the generated connect command on the Mac you want to use. It installs, pairs, and starts the Salesko Local Agent in the background.",
    stepInstallPrereq: "macOS only — Apple silicon or Intel. The installer picks the build for that Mac and verifies its published checksum before installing it.",
    stepInstallGuideLink: "Open the install guide",
    setupExisting: "Already installed? This setup command pairs the computer and configures background startup.",
    updateTitle: "Update Agent · keep pairing",
    updateBody: "Run this in Terminal on the paired computer to install the latest version and restart its background service. Your existing pairing is preserved; no new pairing code is needed.",
    startupTitle: "Startup configuration",
    startupBody: "Run this on an already paired computer to repair or refresh its background service.",
    createPairingCode: "Create pairing code",
    copy: "Copy",
    copied: "Copied",
    pairingCodeExpiresAt: (expiresAt) => `Code expires ${expiresAt}`,
    stepWaitingTitle: "2 · Waiting for the computer",
    stepWaitingBody: "Run the command above. The computer shows up here as soon as it checks in.",
    emptyTitle: "No computer connected",
    empty: "Create a pairing code to connect the computer your Private Agent will run on.",
    presenceIsHint: "Presence is a short-lived TTL hint. It does not confirm readiness or execution.",
    presenceHint: (level, expiresAt) => `${level} hint until ${expiresAt}`,
    noPresenceHint: "No current presence hint",
    online: "Online",
    offline: "Offline",
    installedRelease: (version) => `Installed version: ${version}`,
    installedReleaseUnavailable: "Installed version: not reported",
    latestRelease: (version) => `Latest version: ${version}`,
    latestReleaseLoading: "Latest version: loading",
    latestReleaseUnavailable: "Latest version: unavailable",
    runtimes: "Detected runtimes",
    noRuntimeFacts: "This computer has not reported which runtimes it has.",
    signedIn: "signed in",
    notSignedIn: "not signed in",
    revoked: "Revoked",
    revoke: "Revoke",
    keysStayOnComputer: "Provider sign-in and API keys live on the computer. Run this there:",
    updateError: "Could not update your computers. Try again shortly."
  },
  privateAgent: {
    ariaLabel: "Private Agent",
    title: "Private Agent",
    description: "Your own agent, bound to one computer, using your own runtime sign-in. Salesko never sees those credentials.",
    signIn: "Sign in to set up your Private Agent.",
    loading: "Loading your Private Agent",
    name: "Agent name",
    namePlaceholder: "Name your agent",
    computer: "Computer",
    computerHint: "The agent runs on exactly one connected computer.",
    runtime: "Runtime",
    runtimeHint: "Whichever you pick, the sign-in stays on your computer.",
    piVendor: "Provider preset",
    piVendorHint: "Choose a vendor to list its models and prepare its local configuration command. The profile ID below must match the configuration on your computer.",
    piVendorPlaceholder: "Select a vendor",
    piConfigureHint: "Run this on the Agent computer. The API key is entered at a hidden terminal prompt and stored locally. This configures the profile as the device default.",
    subscriptionModel: "Model",
    runtimeDefaultModel: "Use runtime default",
    specifyModel: "Specify model",
    modelId: "Model alias or ID",
    subscriptionModelHint: "Enter a model alias or ID supported by this Agent and your account. Aliases may point to newer versions over time. Unavailable models fail at execution.",
    piProvider: "Pi profile ID",
    piProviderHint: "Exact local profile_ref from salesko-agent keys list; this is not the vendor name.",
    piProviderPlaceholder: "openrouter",
    piModel: "Pi model",
    piModelHint: "Pick one of the vendor's published models. Choose a vendor first.",
    piModelPlaceholder: "Select a model",
    runtimes: {
      claudeSubscription: "Claude subscription",
      codexSubscription: "Codex subscription",
      piProviderKey: "Pi + your provider key"
    },
    researchPreset: "Research preset",
    presetHint: "Regional presets of the one research agent — not separate agents.",
    profiles: {
      default: "Default",
      mainland: "Mainland (CN)",
      taiwan: "Taiwan (TW)",
      hongKong: "Hong Kong (HK)"
    },
    save: "Save Private Agent",
    saved: "Private Agent saved.",
    projectionPending: "Waiting for the bound computer to apply this Profile.",
    projectionSynced: "The bound computer has applied this Profile.",
    projectionFailed: "The bound computer could not apply this Profile.",
    remove: "Delete agent",
    removeConfirm: "Delete this Private Agent? Research stops until you create one again.",
    removeConfirmAction: "Delete",
    removeCancel: "Keep it",
    deviceNotActive: "That computer is no longer active. Pick a connected one.",
    needsRebindingTitle: "Needs re-binding",
    needsRebinding: "The computer this agent was bound to is revoked or gone. Pick another one and save.",
    noComputersTitle: "No computer connected",
    noComputers: "Connect a computer first — a Private Agent has to run somewhere.",
    goToComputers: "Go to Computers",
    loadError: "Could not load your Private Agent. Try again shortly.",
    saveError: "Could not save your Private Agent. Try again shortly."
  },
  privateAgentJobs: {
    ariaLabel: "Private Agent research",
    eyebrow: "Private Agent",
    title: "Private Agent",
    signIn: "Sign in to see what your Private Agent is working on.",
    loading: "Loading your Private Agent",
    loadingJobs: "Loading research",
    loadError: "Could not load your research jobs. Try again shortly.",
    emptyTitle: "No research yet",
    empty: "Open a person or opportunity and ask your Private Agent to research it.",
    noAgentTitle: "No Private Agent yet",
    noAgent: "Name an agent and bind it to a connected computer before it can research anything.",
    setUpAgent: "Set up Private Agent",
    openComputers: "Open Computers",
    computerOffline: "The computer this agent runs on is offline. Queued research starts when it checks in.",
    computerRevoked: "The computer this agent was bound to is revoked. Connect one and re-bind the agent.",
    computer: {
      online: "Computer online",
      offline: "Computer offline",
      revoked: "Computer revoked",
      missing: "No computer bound"
    },
    status: {
      queued: "Queued",
      dispatching: "Handing over",
      running: "Running",
      cancelRequested: "Canceling",
      succeeded: "Ready for review",
      failed: "Failed",
      canceled: "Canceled",
      waitingDevice: "Waiting for your computer",
      erased: "Erased"
    },
    statusDetail: {
      waitingDevice: "No connected computer accepted this research. Connect one, then run it again.",
      queuedComputerOffline: "Queued \u2014 computer offline",
      queuedNoComputer: "Queued \u2014 no connected computer"
    },
    stopReasons: {
      completed: "Finished",
      stepBudget: "Hit the step budget",
      timeBudget: "Hit the time budget",
      tokenBudget: "Hit the token budget",
      providerLimit: "Stopped by a provider limit"
    },
    stopReason: (label) => `Stopped: ${label}`,
    startedAt: (when) => `Started ${when}`,
    jobId: (id) => `Job ${id}`,
    review: "Review changes",
    reviewPending: "Preparing\u2026",
    cancel: "Cancel",
    canceling: "Canceling\u2026",
    showDetails: "Details",
    hideDetails: "Hide details",
    loadingDetails: "Loading details",
    noResultDetail: "This job returned no result.",
    frameConflict: "The graph changed since this research started. Run it again on the current graph.",
    resultNotReady: "This research has no result to review yet.",
    materializeError: "Could not prepare the change set. Try again shortly.",
    changeSetSourceLabel: (agentName) => `${agentName} research`,
    question: (entityLabel) => `Research ${entityLabel} and propose relationship graph updates.`,
    trigger: {
      ariaLabel: "Private Agent research",
      eyebrow: "Private Agent",
      action: (agentName) => `Research with ${agentName}`,
      actionFallback: "Research with your Private Agent",
      starting: "Starting\u2026",
      checkingAccess: "Checking your plan\u2026",
      signedOut: "Sign in to run research on your own computer.",
      entitlementRequired: "Deep Research is included in paid plans.",
      profileRequired: "Name a Private Agent and bind it to a connected computer first.",
      noActiveComputer: "Connect a computer for your Private Agent to run on.",
      activeJob: "Your Private Agent is already on a research job.",
      activeJobConflict: "Your Private Agent is already on a research job. Wait for it or cancel it first.",
      noCommittedFrame: "Commit a graph first \u2014 research proposes changes against a saved graph.",
      viewPlans: "View plans",
      openJobs: "Open Agent",
      insufficientCredits: "Not enough Agent credits to start research. Top up or upgrade your plan.",
      frameVersionUnavailable: "This graph has no version to research against. Reload and try again.",
      createError: "Could not start research. Try again shortly."
    }
  },
  privateAgentWorkspace: {
    recovery: {
      queueLabel: "Turn queue",
      replyDelivered: "Reply delivered",
      ended: "Ended without a reply",
      withdrawn: "Stopped before execution",
      stopPending: "Stop requested; checking execution",
      checking: "Execution still being checked",
      needsAttention: "Reply not received; needs attention",
      notStarted: "This turn did not start",
      messageHeld: "Message held; needs attention",
      messageRefused: "Message refused",
      cleanupUnknown: "Execution ended; cleanup unconfirmed",
      retryTurn: "Retry this turn",
      endTurn: "End this turn",
      stopAndSend: "Stop and send",
      retryPending: "Retry the same request",
      pendingRequest: "The response is unconfirmed. The saved request will be sent again with the same identity and target.",
      queueFull: "The queue is full. Finish a turn before sending another.",
      actionConflict: "The target changed. Refresh before choosing another action.",
      storageFailure: "Request not sent: browser storage is unavailable or invalid. Refresh to check it.",
      queueCount: (count) => `${count} of 8 unsettled turns`
    },
    ariaLabel: "Private Agent workspace",
    conversationsAriaLabel: "Private Agent conversations",
    facetsAriaLabel: "Private Agent work views",
    chat: "Chat",
    research: "Research",
    recentConversations: "Recent conversations",
    noConversations: "No conversations yet.",
    loadingConversations: "Loading conversations",
    newConversation: "New conversation",
    creatingConversation: "Creating...",
    messageCount: (count) => `${count} ${count === 1 ? "message" : "messages"}`,
    identitySubtitle: "Works on your own computer. Answers stay with you.",
    deviceLine: (deviceName, state) => `Runs on ${deviceName} · ${state}`,
    computerState: {
      online: "Online",
      offline: "Offline",
      revoked: "Removed",
      missing: "Not connected"
    },
    selectConversationTitle: "Start or pick a conversation",
    selectConversation: "Pick a conversation on the left, or start a new one.",
    setupTitle: "Set up your assistant first",
    profileRequired: "Give your assistant a name and pick a computer for it before you start a conversation.",
    computerOfflineTitle: "Your computer is not online",
    updateTitle: "The assistant on your computer needs an update or a sign-in",
    signInTitle: "Sign in to use your assistant",
    signIn: "Sign in to view or start conversations with your assistant.",
    openComputers: "View computers",
    loadingConversation: "Loading conversation",
    transcriptAriaLabel: "Conversation transcript",
    emptyTranscript: "Ask it something, for example: pull together everything recent with Acme.",
    composerLabel: "Message your assistant",
    composerPlaceholder: "Message your assistant...",
    send: "Send",
    sending: "Sending...",
    checkExecution: "Check again",
    cancelTurn: "Stop",
    cancelingTurn: "Stopping...",
    elapsed: (value) => `${value} elapsed`,
    progressThinking: "Thinking",
    progressUsingTool: "Using a tool",
    progressWaitingApproval: "Waiting for your approval",
    boundDeviceRevokedNotice: "The computer this conversation used is no longer available. Start a new conversation.",
    readiness: {
      profile_missing: "Give your assistant a name and pick a computer for it before you start chatting.",
      device_missing: "The computer your assistant used is no longer in your list. Connect it again under Computers, or pick one that is already connected.",
      device_revoked: "The computer your assistant used was removed. Pick a connected computer under Computers.",
      device_offline: "Your assistant's computer is not online. Bring it online and try again.",
      agent_upgrade_required: (installedRelease) =>
        installedRelease
          ? `The Salesko Agent on your computer is too old (${installedRelease}). Update it and try again.`
          : "The Salesko Agent on your computer is too old. Update it and try again.",
      runtime_missing: "Claude or Codex is not installed on your computer. Install it, sign in, then try again.",
      runtime_not_authenticated: "Claude or Codex on your computer is not signed in. Sign in from that computer's terminal, then try again.",
      toolset_not_configured: "The Salesko Agent on your computer needs an update and a restart.",
      runtime_unsupported_for_tools: "The model tools this conversation uses cannot read your Salesko data. Start a new conversation.",
      profile_identity_unavailable: "This conversation's assistant is no longer set up. Set the same assistant up again, then send."
    },
    agent: "Assistant",
    you: "You",
    youInitial: "Y",
    turnStatus: {
      blocked: "Needs attention",
      queued: "Queued",
      dispatching: "Reaching your computer",
      running: "Working",
      cancel_requested: "Stopping",
      succeeded: "Done",
      failed: "Failed",
      canceled: "Stopped"
    },
    errors: {
      activeConflict: "Your assistant is still working on your last message. Wait for its reply before sending another.",
      profileRequired: "Finish setting up your assistant before you start a conversation.",
      replayGap: "This conversation needs a refresh.",
      reconcileFailed: "Could not confirm what your assistant is doing. Try again.",
      chatPaused: "Chat is temporarily paused. Please try again later.",
      requestFailed: "Your assistant is unavailable right now. Try again shortly."
    },
    researchEmptyTitle: "No research yet",
    researchEmpty: "Open a person or opportunity and ask your assistant to research it.",
    targetUnavailable: "Research target unavailable",
    contextAriaLabel: "People, companies and deals loaded from this conversation",
    touchedEntitiesTitle: "People, companies and deals loaded from this conversation",
    touchedEntitiesEmpty: "The assistant will list the people, companies and deals it looks at while answering.",
    touchedEntitiesError: "These names could not be loaded. The conversation itself is unaffected.",
    touchedEntityUnavailable: "Unavailable",
    openEntityInGraph: (label) => `Open ${label} in the graph`,
    detailTabsAriaLabel: "Private Agent side panel views",
    detailTab: "Detail",
    conversationTab: "This conversation"
  },

  dataTools: {
    ariaLabel: "Advanced data tools",
    title: "Advanced data tools",
    import: "Import",
    export: "Export",
    csvChangeSet: "CSV change set",
    jsonFrame: "JSON frame",
    obsidianFiles: "Obsidian files",
    activeFrame: (frameRecordId) => `Active frame ${frameRecordId}`,
    needsCommittedFrame: "Open Settings from a committed workspace frame to enable frame-scoped tools."
  },
  tenantAdmin: {
    workspaceAriaLabel: "Workspace",
    workspaceTitle: "Workspace",
    signInWorkspace: "Sign in to manage your workspace.",
    loadingWorkspace: "Loading workspace",
    workspaceName: "Workspace name",
    saveName: "Save name",
    workspaceNameSaved: "Workspace name saved.",
    agentSettingsAriaLabel: "Agent settings",
    agentDefaults: "Generic Agent defaults",
    signInAgentDefaults: "Sign in to manage Generic Agent defaults.",
    checkingAccess: "Checking your paid plan before unlocking Generic Agent defaults.",
    defaultsFrozen: "Generic Agent defaults are frozen until your plan can be verified.",
    defaultsLocked: "Generic Agent defaults unlock once a paid subscription is active.",
    loadingDefaults: "Loading Generic Agent defaults",
    language: "Language",
    region: "Region",
    timezone: "Timezone",
    saveDefaults: "Save Generic Agent defaults",
    defaultsSaved: "Generic Agent defaults saved.",
    dataExportAriaLabel: "Data export",
    dataExport: "Data export",
    dataExportDescription: "Download a ZIP of everything in your workspace — graph, notes, and audit trail.",
    exportZip: "Export ZIP",
    exportStarted: "Workspace ZIP export started.",
    requestFailed: "Workspace request failed.",
    locales: {
      en: "English",
      "zh-CN": "简体中文",
      "zh-TW": "繁體中文"
    }
  },
  canonicalRegistry: {
    eyebrow: "Canonical registry",
    allTitle: "Canonical identities",
    count: (count) => `${count} tenant ${count === 1 ? "identity" : "identities"}`,
    signedInRegistry: "Signed-in tenant registry",
    signIn: "Sign in with a tenant session to browse canonical identities across committed frames.",
    unavailable: "Canonical registry is unavailable right now.",
    loading: "Loading canonical identities...",
    noSearchMatch: "No canonical identities match the current search.",
    empty: "No canonical identities yet.",
    openIdentity: "Open identity",
    openIdentityAriaLabel: (label) => `Open ${label}`,
    projectedCounts: (nodeCount, frameCount) =>
      `${nodeCount} projected ${nodeCount === 1 ? "node" : "nodes"} across ${frameCount} ${frameCount === 1 ? "frame" : "frames"}`,
    columns: {
      actions: "Actions",
      confidence: "Confidence",
      frames: "Frames",
      identity: "Identity",
      lastSeen: "Last seen",
      nodes: "Nodes",
      type: "Type"
    }
  },
  mobile: {
    graph: "Graph",
    inbox: "Inbox",
    you: "You",
    listView: "List",
    graphCaption: "Tap a node — bigger dots have more connections",
    graphNodeAriaLabel: (label, typeLabel) => `${label}, ${typeLabel}`,
    graphSelfNodeAriaLabel: (label) => `${label}, you`,
    primaryNavAriaLabel: "Primary",
    notificationsTitle: "Notifications",
    captureMeeting: "Capture a meeting",
    selectedEntity: "Selected entity",
    close: "Close",
    clearSearch: "Clear search",
    relationshipReminder: "Relationship reminder",
    relationshipReminderBody: (sourceLabel, targetLabel, days) =>
      `${sourceLabel} -> ${targetLabel} has gone ${days} days past its touchpoint window.`,
    opportunitySignal: "Opportunity",
    contractEmit: "the Agent builds nodes, relationships, timeline, and view.",
    generatingGraphFrame: "Generating graph frame...",
    generateGraphFrame: "Generate graph frame",
    noDatedEvents: "No dated events in this frame yet.",
    undated: "Undated",
    linkedEntitiesCount: (count) => `Linked entities · ${count}`,
    thisWeek: "Across saved graph history",
    opportunityHero: "Review new opportunities, save the useful ones, and add unscheduled follow-ups.",
    activeSignalsAcross: (opportunityCount) => `${opportunityCount} opportunities currently shown`,
    opportunityFeed: "Opportunity inbox",
    signInSignalsBody: "Your private opportunity inbox appears here after sign-in.",
    emptySignalsBody: "No pending or saved opportunities are available.",
    updatedAt: (date) => `Updated ${date}`,
    evidenceCount: (count) => `${count} evidence`,
    entityCount: (count) => `${count} entities`,
    frame: "Frame",
    notesCaptured: "Notes you captured. The Agent structured each into graph nodes.",
    committedFrames: "Committed frames",
    current: "Current",
    frameNodeSummary: (nodeCount, date) => `${nodeCount} nodes · ${date}`,
    noCommittedFrames: "No committed frames",
    signInToSync: "Sign in to sync tenant graph access",
    installedOnThisDevice: "Installed on this device",
    installSalesko: "Install Salesko",
    installedPwaDescription: "Opens full-screen · works offline",
    installReadyDescription: "Add to your home screen — full-screen, offline-ready",
    installFallbackDescription: "Share -> Add to Home Screen for the app experience",
    install: "Install",
    mcpAccess: "MCP access",
    signInRequired: "Sign in required",
    proPlanRequired: "Pro plan required",
    signalsAndReminders: "Signals & reminders",
    tenantFeedActive: "Tenant feed active",
    inputLanguage: "Input language",
    privacy: "Privacy",
    localPreview: "Local preview",
    graphCachedOffline: "Your graph is cached — readable with no connection.",
    markAllRead: "Mark all read",
    refreshingSignals: "Refreshing signals...",
    noNotificationsTitle: "No notifications",
    notificationsEmpty: "Relationship reminders and opportunity signals will appear here.",
    browserDefault: "Browser default",
    noMatchesFor: (query) => `No matches for "${query}"`
  },
  connectors: {
    navigationAriaLabel: "Connectors navigation",
    categoriesAriaLabel: "Connector categories",
    brandLabel: "Connectors",
    eyebrow: "Integrations",
    title: "App Connectors",
    description:
      "Two directions, one graph: message the agent inbound to capture notes on the go, or let it read outbound from the accounts you already use.",
    searchPlaceholder: "Search connectors...",
    noMatchesTitle: "No matches",
    noMatchesBody: (categoryLabel) => `No ${categoryLabel.toLowerCase()} connectors match your search.`,
    tierPro: "Pro",
    comingSoon: "Coming soon",
    premium: "Premium",
    upgradeToUnlock: "Upgrade to unlock",
    available: "Available",
    connect: "Connect",
    connected: "Connected",
    manage: "Manage",
    disconnect: "Disconnect",
    connectError: "Could not update your Gmail connection. Please try again.",
    calendarConnectError: "Calendar connection failed. Please try again.",
    connectedAccount: (email) => `Connected as ${email}`,
    oneTimeConnected: "Gmail connected.",
    oneTimeError: "Gmail connection failed. Please try again.",
    syncNow: "Sync now",
    syncing: "Syncing…",
    syncQueued: "Sync queued — new leads will appear shortly.",
    calendarSyncQueued: "Calendar sync queued.",
    syncCompleted: "Sync complete.",
    syncError: "Could not start a Gmail sync. Please try again.",
    calendarSyncError: "Could not start Calendar sync. Please try again.",
    lastSyncedAt: (date) => `Last synced ${date}`,
    lastSyncFailed: (message) => (message ? `Last sync failed: ${message}` : "Last sync failed."),
    reconnectRequired: "Google access was revoked. Reconnect to keep syncing leads.",
    direction: {
      inbound: "Inbound · note capture",
      outbound: "Outbound · data source"
    },
    lockCopy: {
      signed_out: "Sign in to unlock premium connectors.",
      checking: "Checking paid Sidecar access before unlocking premium connectors.",
      verify_failed: "Premium connectors stay locked until Sidecar access can be verified.",
      locked: "Premium connectors unlock after a paid Sidecar subscription is active."
    },
    categories: {
      messaging: {
        eyebrow: "Note capture",
        label: "Messaging",
        blurb: "Message your personal Agent directly on these channels — it replies in the same thread."
      },
      email: {
        eyebrow: "Inbox intelligence",
        label: "Email",
        blurb: "Let the Agent read your mail so threads become searchable relationship notes."
      },
      contacts: {
        eyebrow: "People sync",
        label: "Contacts",
        blurb: "Bring the people you already know into your graph automatically."
      },
      calendar: {
        eyebrow: "Meeting signals",
        label: "Calendar",
        blurb: "Sync your calendar so every meeting and attendee becomes a relationship signal in your graph."
      }
    },
    items: {
      telegram: {
        displayName: "Telegram",
        tagline: "Message the agent on Telegram to capture notes on the go — it replies in the same thread."
      },
      wechat: {
        displayName: "微信 WeChat",
        tagline: "Send the agent a WeChat message to log a note or contact — it answers right where you typed."
      },
      line: {
        displayName: "Line",
        tagline: "Drop the agent a Line message to capture a meeting note — the reply lands in the same chat."
      },
      gmail: {
        displayName: "Gmail",
        tagline: "Let the agent read and file your Gmail so email threads become searchable relationship notes."
      },
      "microsoft-email": {
        displayName: "Microsoft Email",
        tagline: "Let the agent read Outlook mail so conversations become searchable relationship signals."
      },
      "google-contacts": {
        displayName: "Google Contacts",
        tagline: "Sync Google Contacts so the people you already know show up in your graph automatically."
      },
      "google-calendar": {
        displayName: "Google Calendar",
        tagline: "Sync Google Calendar so meetings and attendees flow into your graph as event signals."
      },
      "microsoft-calendar": {
        displayName: "Microsoft Calendar",
        tagline: "Sync Outlook / Microsoft Calendar so meetings and attendees flow into your graph."
      },
      "apple-calendar": {
        displayName: "Apple Calendar",
        tagline: "Coming soon — sync iCloud Calendar via CalDAV."
      }
    },
    replyTemplate: {
      eyebrow: "Reply automation",
      heading: "Gmail reply templates",
      subtitle: "Give the Agent a user-approved template library to choose from when replies are enabled.",
      newTemplate: "New template",
      emptyTitle: "No templates yet",
      enabledLabel: "Available to Agent",
      enabledHint: "Only enabled templates are eligible for Agent selection.",
      requiresReviewLabel: "Require review",
      requiresReviewHint: "Keep this on until the final send flow is explicitly approved.",
      nameLabel: "Name",
      namePlaceholder: "Pricing inquiry",
      descriptionLabel: "When to use",
      descriptionPlaceholder: "Use when a prospect asks about pricing or procurement.",
      subjectLabel: "Subject",
      subjectPlaceholder: "Re: {{subject}}",
      bodyLabel: "Body",
      bodyPlaceholder: "Hi {{senderName}},\n\nThanks for reaching out. I will review this and follow up shortly.\n\nBest,\n{{myName}}",
      variablesHint: "Available variables: {{senderName}}, {{senderEmail}}, {{subject}}, {{myName}}",
      create: "Create template",
      creating: "Creating…",
      save: "Save template",
      saving: "Saving…",
      saved: "Template saved.",
      createError: "Could not create the template. Please try again.",
      saveError: "Could not save the template. Please try again.",
      loadError: "Could not load your saved templates.",
      updatedAt: (date) => `Updated ${date}`
    },
    candidates: {
      eyebrow: "Inbox intelligence",
      heading: "Lead candidates",
      subtitle: "Emails the Agent flagged as real prospects, waiting for your review.",
      emptyTitle: "No lead candidates yet",
      emptyBody: "Sync your Gmail account and the Agent will surface real prospects here.",
      confidence: (percent) => `${percent}% confidence`,
      draftReply: "Draft reply",
      draftReplyPending: "Drafting…",
      draftReplyCreated: "Gmail draft created. Review it in Gmail before sending.",
      draftReplyError: "Could not create a Gmail draft. Please try again.",
      draftReconnectRequired: "Reconnect Gmail to allow draft creation.",
      confirm: "Confirm",
      confirmPending: "Confirming…",
      confirmError: "Could not confirm this candidate. Please try again.",
      dismiss: "Dismiss",
      dismissPending: "Dismissing…",
      dismissError: "Could not dismiss this candidate. Please try again.",
      needsFrame: "Open the workspace and save a graph frame before confirming candidates.",
      frameConflictRetry: "The graph changed while confirming. Refresh and try again."
    }
  },
  schedule: {
    eyebrow: "Read-only agenda",
    title: "Schedule",
    range: (from, to) => `${from} – ${to}`,
    items: (count) => `${count} ${count === 1 ? "item" : "items"}`,
    signInToLoad: "Sign in to load your private Schedule agenda.",
    loading: "Loading Schedule…",
    unavailable: "Schedule is temporarily unavailable.",
    calendarTitle: "Calendar events",
    calendarEmpty: "No calendar events in this range.",
    untitledEvent: "Untitled calendar event",
    timeUnavailable: "Time unavailable",
    followUpsTitle: "Unscheduled follow-ups",
    followUpsEmpty: "No follow-ups are waiting to be scheduled.",
    followUpRequested: (date) => `Follow-up requested ${date}`,
    remindersTitle: "Relationships to revisit",
    remindersEmpty: "No stale relationships need attention.",
    lastSeen: (date) => `Last seen ${date}`,
    staleByDays: (days) => `${days} ${days === 1 ? "day" : "days"} overdue`,
    truncated: "More items exist outside this bounded response.",
    close: "Close Schedule"
  },
  currentLeads: {
    eyebrow: "Active pipeline",
    title: "Current",
    activeCount: (count) => `${count} in Current`,
    emptyTitle: "Nothing in Current yet",
    emptyBody: "Confirm an email lead candidate or mark any node as Current to track it here.",
    viewInGraph: "View in graph",
    removeFromCurrent: "Remove from Current",
    sourceEmail: "From email"
  },
  mcpConsent: {
    title: "Authorize MCP access",
    description: "Choose what this MCP client can access in your Salesko graph, then continue to grant access.",
    scopeGraphReadLabel: "Read your graph",
    scopeGraphReadHint: "Search and read people, companies, events, and opportunities.",
    scopeCalendarReadLabel: "Read your calendar",
    scopeCalendarReadHint: "List and search upcoming calendar events.",
    scopeGraphWriteLabel: "Propose graph changes",
    scopeGraphWriteHint: "Submit change-set proposals for human review; nothing is applied automatically.",
    continue: "Continue",
    signInBody: "Sign in to grant this MCP client access to your Salesko graph.",
    invalidLinkHeading: "Invalid consent link",
    invalidLinkBody: "This MCP consent link is missing or invalid. Start from your MCP client again.",
    authNotConfiguredBody: "Sign-in is not configured for this Salesko deployment, so MCP access cannot be granted.",
    issuanceFailed: "Could not grant MCP access. Please try again."
  },
  meetingPreparation: {
    ariaLabel: "Meeting preparation",
    title: "Meeting preparation",
    prepareAction: "Prepare brief",
    prepareActionTitle: "Prepare a meeting brief for this event",
    preparePending: "Preparing…",
    idleBody: "Generate an AI brief for this meeting: goal, participants, relationship signals, and suggested questions.",
    loading: "Preparing your meeting brief…",
    successHeading: "Meeting brief",
    goalTitle: "Goal",
    participantsTitle: "Participants & known context",
    knownContextEmpty: "No additional context beyond the participants.",
    relationshipSignalsTitle: "Relationship signals",
    relationshipSignalsEmpty: "No relationship signals in your graph yet.",
    watchoutsTitle: "Watchouts",
    watchoutsEmpty: "No watchouts identified.",
    suggestedQuestionsTitle: "Suggested questions",
    suggestedCloseTitle: "Suggested close",
    sourcesTitle: "Sources",
    sourcesTruncated: "Some sources were left out of this bounded response.",
    sourceCalendarEvent: "Calendar event",
    sourceCalendarAttendee: "Calendar attendee",
    sourceGraphEdge: "Relationship",
    sourceEvidence: "Evidence",
    sourceFrameProvenance: (frameId, acceptedAt) => `Frame ${frameId} · accepted ${acceptedAt}`,
    gapsTitle: "Gaps",
    gapsEmpty: "No gaps identified — the brief has complete evidence.",
    gapNoGraphPersonMatch: (participantLabel) => `No graph match found for ${participantLabel}.`,
    gapNoRelationshipEvidence: "No relationship evidence was available in your graph.",
    gapNoOpportunityEvidence: "No opportunity evidence was available in your graph.",
    gapContextTruncated: (collections) => `Some context was truncated: ${collections}.`,
    gapOptionalCalendarDetailUnavailable: (detail) => `${detail} was not available on this calendar event.`,
    calendarDetailLabels: {
      summary: "Summary",
      location: "Location",
      endAt: "End time",
      organizer: "Organizer"
    },
    truncatedCollectionLabels: {
      calendarAttendees: "Calendar attendees",
      graphPeople: "Graph people",
      graphContextNodes: "Graph context",
      graphEdges: "Graph relationships",
      opportunities: "Opportunities",
      evidence: "Evidence"
    },
    errorEntitlement: "Meeting preparation requires a paid plan. Upgrade to generate briefs.",
    errorNotEligible: "This event is not eligible for a meeting brief.",
    errorStaleEvent: "This calendar event is out of date. Sync your calendar and try again.",
    errorModelError: "Could not generate a brief right now. Please try again.",
    errorGeneric: "Could not prepare this meeting. Please try again."
  }
} satisfies Dictionary;
